import * as THREE from "../../../libs/three.js/build/three.module.js";
import {createSurfaceFit, estimateSurface, orientTowards} from "./surfaceEstimator.js";

// "Forreste lag-pick" - the method behind "Overfladedetektion" (it replaced an earlier neighbourhood/PCA pipeline).
//
// THE PROBLEM IT SOLVES - pick-through. A point cloud is discrete, so a cursor that visually sits on a
// foreground surface can lie between its points. Potree's pick (PointCloudOctree.pick) renders point indices
// with a depth test and then takes, in a window around the cursor, the hit that is NEAREST TO THE CURSOR IN
// PIXELS - depth is ignored. A pixel in a hole therefore shows a point of the surface BEHIND (distance 0) and
// wins over a foreground point three pixels away.
//
// THE METHOD. The pick buffer already holds, per pixel, the front-most splat of that pixel (depth test).
// So ONE pick with a small window (21x21) and all pixels' hits gives the front-most points around the cursor.
// For each distinct point the depth along the view ray is t = (P - O) . dir. Sorted by t, the nearest depth
// layer that is supported by at least `support` distinct points is the FOREGROUND LAYER; everything clearly
// behind it (more than a tolerance) is discarded. The position is the layer point nearest to the cursor (in
// pixels), or optionally the ray/plane intersection with the layer's local plane. A normal for the disc comes
// from <= 32 layer points (the existing Jacobi estimator - a few hundred flops, not a node scan).
//
// Depth tolerance - how it is determined: the layer must contain the points of a SLOPED surface that falls
// inside the window, and must exclude a different surface behind it. A surface seen at slope s over a window
// of radius R px has a depth spread of about R * pixelWorldSize(t) * tan(s); tol = depthTolFactor * R *
// pixelWorldSize(t_seed) (factor 2 allows up to ~63 deg of slope), i.e. it scales with distance and field of
// view and not with a hardcoded metre value; `minTol` is only a floor for noise on very near surfaces.
//
// Support rule - the compromise: `support` distinct points protect against a single floating noise point; a
// layer that cannot be supported by any seed (very sparse LOD / thin object with one point) falls back to the
// nearest hit and the result is flagged `lowConfidence` instead of pretending to be a surface.

export const FRONT_PARAMS = {
	window: 21,             // px (odd): pick window around the cursor
	pickMinSize: 1,         // px: minimum splat size in the PICK pass only (1 = no inflation, see PointCloudOctree.pick)
	depthTolFactor: 2,      // see header
	minTol: 0.02,           // m, floor of the depth tolerance
	support: 2,             // min distinct points in the foreground layer
	maxPixelRadius: 10,     // px: farthest layer point that may be used (window radius)
	placement: "point",     // "point": snap to a real foreground point | "plane": ray / local plane intersection
	planeMinPoints: 4,
	planeMaxPoints: 32,
	planeMinPlanarity: 0.6,
};

// ── pure selection (Level 1 testable: plain numbers, no THREE/DOM) ─────────────────────────────────────
// hits: [{x,y,z,t,d2,...}] t = depth along the view ray (m), d2 = squared pixel distance to the cursor.
// tolAt(t) -> depth tolerance (m). Returns {layer, seed, supported, lowConfidence, nearest}
export function selectFrontLayer(hits, tolAt, params = FRONT_PARAMS){
	const r2 = params.maxPixelRadius * params.maxPixelRadius;
	const list = [];
	for(const h of hits) if(h.d2 <= r2) list.push(h);
	if(list.length === 0) return null;
	list.sort((a, b) => a.t - b.t);

	let seedIndex = -1, end = -1;
	for(let k = 0; k < list.length; k++){
		const limit = list[k].t + tolAt(list[k].t);
		let m = k;
		while(m + 1 < list.length && list[m + 1].t <= limit) m++;
		if(m - k + 1 >= params.support){ seedIndex = k; end = m; break; }
	}
	let lowConfidence = false;
	if(seedIndex < 0){
		// no depth layer is supported: take the nearest hit's layer anyway, but say so
		seedIndex = 0; lowConfidence = true;
		const limit = list[0].t + tolAt(list[0].t);
		end = 0;
		while(end + 1 < list.length && list[end + 1].t <= limit) end++;
	}
	const layer = list.slice(seedIndex, end + 1);
	let nearest = layer[0];
	for(const h of layer) if(h.d2 < nearest.d2) nearest = h;
	return {layer, seed: list[seedIndex], nearest, supported: !lowConfidence, lowConfidence, candidates: list.length};
}

// ── driver ─────────────────────────────────────────────────────────────────────────────────────────────────
export function createFrontContext(){
	return {
		raycaster: new THREE.Raycaster(),
		size: new THREE.Vector2(),
		hits: [],
		buf: new Float32Array(3 * FRONT_PARAMS.planeMaxPoints),
		fit: createSurfaceFit(),
		dir: new THREE.Vector3(),
		origin: new THREE.Vector3(),
		stats: {picks: 0, lastPickMs: 0, lastSelectMs: 0, lastTotalMs: 0, lastHits: 0, lastLayer: 0, lastCandidates: 0, lowConfidence: 0, noHit: 0, plane: 0},
	};
}

function pixelWorldSize(camera, t, heightPx, widthPx){
	if(camera.isOrthographicCamera){
		return Math.abs(camera.top - camera.bottom) / (camera.zoom || 1) / heightPx;
	}
	return 2 * t * Math.tan((camera.fov * Math.PI / 180) / 2) / heightPx;
}

// ONE pick per point cloud (lean, all pixels of the small window), then the pure selection. `mouse` is
// canvas-relative px. Returns a result object (ctx-owned scratch for `location`/`normal` - copy if kept) or null.
export function pickFrontSurface(viewer, mouse, camera, clouds, params, ctx, now = () => performance.now()){
	const t0 = now();
	const renderer = viewer.renderer;
	const clientW = renderer.domElement.clientWidth, clientH = renderer.domElement.clientHeight;
	const nmouse = {x: (mouse.x / clientW) * 2 - 1, y: -(mouse.y / clientH) * 2 + 1};
	ctx.raycaster.setFromCamera(nmouse, camera);
	const ray = ctx.raycaster.ray;
	const O = ctx.origin.copy(ray.origin), D = ctx.dir.copy(ray.direction);

	const pickParams = {
		pickClipped: true, all: true, lean: true,
		pickWindowSize: params.window, pickMinSize: params.pickMinSize,
		x: mouse.x, y: clientH - mouse.y,
	};

	const hits = ctx.hits; hits.length = 0;
	for(const cloud of clouds){
		if(cloud.visible === false) continue;
		const arr = cloud.pick(viewer, camera, ray, pickParams);
		ctx.stats.picks++;
		if(!arr) continue;
		for(const p of arr){
			const P = p.position;
			hits.push({x: P.x, y: P.y, z: P.z, t: (P.x - O.x) * D.x + (P.y - O.y) * D.y + (P.z - O.z) * D.z, d2: p.distanceToCenter, cloud, level: p.level});
		}
	}
	const t1 = now();
	ctx.stats.lastPickMs = t1 - t0;
	ctx.stats.lastHits = hits.length;

	const tolAt = (t) => Math.max(params.minTol, params.depthTolFactor * params.maxPixelRadius * pixelWorldSize(camera, t, clientH, clientW));
	const sel = selectFrontLayer(hits, tolAt, params);

	// non-point geometry (IFC/OBJ) in front of the layer wins, as in the generic pick
	let meshHit = null;
	const meshes = (viewer.scene && viewer.scene.pickableMeshes) || [];
	if(meshes.length > 0){
		const m = ctx.raycaster.intersectObjects(meshes, false)[0];
		if(m && (!sel || m.distance < sel.seed.t)) meshHit = m;
	}

	const out = ctx.result || (ctx.result = {ok: false, location: new THREE.Vector3(), normal: new THREE.Vector3(0, 0, 1)});
	out.ok = false; out.accepted = false; out.mesh = false; out.lowConfidence = false; out.pointcloud = null; out.usedCenter = false;
	out.layerSize = 0; out.hitCount = hits.length; out.candidates = sel ? sel.candidates : 0; out.tFront = NaN; out.fit = null;

	if(meshHit){
		out.ok = true; out.mesh = true; out.location.copy(meshHit.point); out.tFront = meshHit.distance;
		out.normal.copy(O).sub(meshHit.point).normalize();
		ctx.stats.lastTotalMs = now() - t0;
		return out;
	}
	if(!sel){
		ctx.stats.noHit++;
		ctx.stats.lastTotalMs = now() - t0;
		return null;
	}

	const nearest = sel.nearest;
	out.ok = true; out.pointcloud = nearest.cloud; out.layerSize = sel.layer.length; out.tFront = sel.seed.t;
	out.lowConfidence = sel.lowConfidence; out.usedCenter = nearest.d2 === 0;
	if(sel.lowConfidence) ctx.stats.lowConfidence++;
	out.location.set(nearest.x, nearest.y, nearest.z);
	ctx.stats.lastLayer = sel.layer.length; ctx.stats.lastCandidates = sel.candidates;

	// local plane/normal from the (<= 32) layer points nearest to the cursor
	const n = Math.min(sel.layer.length, params.planeMaxPoints);
	if(n >= params.planeMinPoints){
		const layer = sel.layer.length > n ? sel.layer.slice().sort((a, b) => a.d2 - b.d2).slice(0, n) : sel.layer;
		const buf = ctx.buf;
		for(let i = 0; i < n; i++){
			buf[3 * i] = layer[i].x - nearest.x; buf[3 * i + 1] = layer[i].y - nearest.y; buf[3 * i + 2] = layer[i].z - nearest.z;
		}
		const fit = estimateSurface(buf, n, ctx.fit);
		out.fit = fit;
		if(fit.ok && fit.planarity >= params.planeMinPlanarity){
			orientTowards(fit, O.x - nearest.x, O.y - nearest.y, O.z - nearest.z);
			out.normal.set(fit.nx, fit.ny, fit.nz);
			out.accepted = true;
			if(params.placement === "plane"){
				const denom = D.x * fit.nx + D.y * fit.ny + D.z * fit.nz;
				if(Math.abs(denom) > 0.15){
					const tt = ((nearest.x - O.x) * fit.nx + (nearest.y - O.y) * fit.ny + (nearest.z - O.z) * fit.nz) / denom;
					const px = O.x + D.x * tt, py = O.y + D.y * tt, pz = O.z + D.z * tt;
					const lim = 1.5 * params.maxPixelRadius * pixelWorldSize(camera, tt, clientH, clientW);
					if(Math.hypot(px - nearest.x, py - nearest.y, pz - nearest.z) <= lim){
						out.location.set(px, py, pz);
						ctx.stats.plane++;
					}
				}
			}
		}
	}
	if(!out.accepted) out.normal.set(O.x - nearest.x, O.y - nearest.y, O.z - nearest.z).normalize();

	const t2 = now();
	ctx.stats.lastSelectMs = t2 - t1;
	ctx.stats.lastTotalMs = t2 - t0;
	return out;
}
